// ============================================================
// Chess Arena — Debug Menu
// ============================================================
// Overlay that lets developers skip to any screen or level.
// Toggle with backtick (`) key.
// ============================================================

import type { Screen } from '../screenManager';

export class DebugMenu implements Screen {
  name = 'debug';
  private container!: HTMLElement;
  private onNavigate: ((action: string, params?: Record<string, unknown>) => void) | null = null;

  init(container: HTMLElement): void {
    this.container = container;
    this.container.innerHTML = this.buildHTML();
    this.bindEvents();
  }

  setOnNavigate(cb: (action: string, params?: Record<string, unknown>) => void): void {
    this.onNavigate = cb;
  }

  show(): void {
    // nothing extra needed
  }

  hide(): void {
    // nothing extra needed
  }

  private buildHTML(): string {
    // Build level buttons for levels 1–20
    let levelButtons = '';
    for (let i = 1; i <= 20; i++) {
      const hasTutorial = i <= 5;
      const label = hasTutorial ? `Level ${i} 📖` : `Level ${i}`;
      levelButtons += `<button class="debug-btn debug-btn-level" data-action="level" data-level="${i}">${label}</button>`;
    }

    return `
      <div class="debug-overlay">
        <div class="debug-panel">
          <div class="debug-header">
            <h2>Debug Menu</h2>
            <button class="debug-close" data-action="close">✕</button>
          </div>

          <div class="debug-section">
            <h3>Screens</h3>
            <div class="debug-grid">
              <button class="debug-btn" data-action="splash">Splash</button>
              <button class="debug-btn" data-action="mainMenu">Main Menu</button>
              <button class="debug-btn" data-action="dailyTasks">Daily Tasks</button>
              <button class="debug-btn" data-action="chestOpening">Chest Opening</button>
              <button class="debug-btn" data-action="abilityCollection">Abilities</button>
              <button class="debug-btn" data-action="resultVictory">Result: Victory</button>
              <button class="debug-btn" data-action="resultDefeat">Result: Defeat</button>
            </div>
          </div>

          <div class="debug-section">
            <h3>Levels <span class="debug-hint">(📖 = has tutorial)</span></h3>
            <div class="debug-grid debug-grid-levels">
              ${levelButtons}
            </div>
          </div>

          <div class="debug-section">
            <h3>Player State</h3>
            <div class="debug-grid">
              <button class="debug-btn debug-btn-warn" data-action="skipOnboarding">Skip Onboarding</button>
              <button class="debug-btn debug-btn-warn" data-action="resetOnboarding">Reset Onboarding</button>
              <button class="debug-btn debug-btn-danger" data-action="resetSave">Reset All Save Data</button>
              <button class="debug-btn" data-action="addCoins">+500 Coins</button>
              <button class="debug-btn" data-action="addTrophies">+100 Trophies</button>
              <button class="debug-btn" data-action="maxLives">Max Lives</button>
              <button class="debug-btn" data-action="testChests">Test Chests</button>
              <button class="debug-btn" data-action="testMenuTutorial">Test Menu Tutorial</button>
            </div>
          </div>

          <div class="debug-section">
            <h3>Abilities</h3>
            <div class="debug-grid">
              <button class="debug-btn" data-action="unlockAbility" data-ability="bomb">Unlock Bomb</button>
              <button class="debug-btn" data-action="unlockAbility" data-ability="freeze">Unlock Freeze</button>
              <button class="debug-btn" data-action="unlockAbility" data-ability="shield">Unlock Shield</button>
              <button class="debug-btn" data-action="unlockAbility" data-ability="lightning">Unlock Lightning</button>
              <button class="debug-btn" data-action="unlockAllAbilities">Unlock All + 10 Charges</button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private bindEvents(): void {
    this.container.addEventListener('click', (e) => {
      const target = (e.target as HTMLElement).closest('[data-action]') as HTMLElement | null;
      if (!target) return;

      const action = target.dataset.action!;
      if (action === 'close') {
        this.onNavigate?.('close');
        return;
      }

      const params: Record<string, unknown> = {};
      if (action === 'level') {
        params.level = parseInt(target.dataset.level!, 10);
      }
      if (target.dataset.ability) {
        params.ability = target.dataset.ability;
      }

      this.onNavigate?.(action, params);
    });
  }
}
